// Simulador de amortización del plan: cuota semanal fija con IVA, al centavo. Usa el mismo motor
// compartido quoteWeekly (shared/domain) que el wizard de ventas.
import { useState } from 'react';
import { NumberInput } from '@/kit';
import { quoteWeekly } from '@/shared/domain/quote';
import { formatMXN } from '@/shared/lib/format/money';
import { FIELD_INPUT, FIELD_LABEL } from './ui';

export function AmortizationSimulator({
    tna,
    openingFeePct,
    vatRate,
    terms,
    defaultDownPct,
}: {
    /** TNA como fracción anual (2.60 = 260%). */
    tna: number;
    /** Comisión de apertura como fracción. */
    openingFeePct: number;
    /** IVA sobre intereses del plan como fracción (0.16 = 16%). */
    vatRate: number;
    /** Plazos disponibles del plan (semanas). */
    terms: number[];
    /** Enganche inicial en % entero (mín del plan). */
    defaultDownPct: number;
}) {
    const [price, setPrice] = useState(6000);
    const [downPct, setDownPct] = useState(defaultDownPct);
    const [weeks, setWeeks] = useState(terms[0] ?? 12);

    // Si el plazo elegido dejó de existir (editaron los plazos del plan), cae al primero.
    const effectiveWeeks = terms.includes(weeks) ? weeks : (terms[0] ?? 12);
    const result = quoteWeekly({
        price,
        downPaymentPct: downPct,
        weeks: effectiveWeeks,
        tna,
        openingFeePct,
        vatRate,
    });

    return (
        <div className="rounded-md border border-border bg-muted/35 p-2.5">
            <div className="mb-2.5 grid grid-cols-3 gap-2.5">
                <div>
                    <label className={FIELD_LABEL} htmlFor="sim-price">
                        Precio del producto
                    </label>
                    <NumberInput
                        id="sim-price"
                        value={price}
                        min={0}
                        step={100}
                        aria-label="Precio del producto"
                        className="w-full px-2 py-1.5"
                        onValueChange={setPrice}
                    />
                </div>
                <div>
                    <label className={FIELD_LABEL} htmlFor="sim-down-pct">
                        Enganche (%)
                    </label>
                    <NumberInput
                        id="sim-down-pct"
                        value={downPct}
                        min={0}
                        max={100}
                        step={1}
                        aria-label="Enganche (%)"
                        className="w-full px-2 py-1.5"
                        onValueChange={setDownPct}
                    />
                </div>
                <div>
                    <label className={FIELD_LABEL} htmlFor="sim-weeks">
                        Plazo (semanas)
                    </label>
                    <select
                        id="sim-weeks"
                        value={effectiveWeeks}
                        onChange={(e) => {
                            setWeeks(Number(e.target.value));
                        }}
                        className={FIELD_INPUT}
                    >
                        {terms.map((t) => (
                            <option key={t} value={t}>
                                {t} sem
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="mb-2 grid grid-cols-2 gap-2 rounded-[5px] border border-border bg-card px-3 py-2 text-[11px]">
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                        Enganche cliente
                    </span>
                    <span className="text-[13px] font-bold tabular-nums">
                        {formatMXN(result.downPayment)}
                    </span>
                    <span className="text-[10px] tabular-nums text-muted-foreground">
                        ({downPct}% de {formatMXN(price)})
                    </span>
                </div>
                <div className="flex items-baseline justify-end gap-1.5">
                    <span className="text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                        Saldo financiado
                    </span>
                    <span className="text-[13px] font-bold tabular-nums">
                        {formatMXN(result.financed)}
                    </span>
                </div>
            </div>

            <div className="max-h-64 overflow-y-auto rounded-[5px] border border-border">
                <table className="w-full border-collapse bg-card text-[10.5px]">
                    <thead>
                        <tr>
                            {['Sem.', 'Cuota', 'Capital', 'Interés', 'IVA', 'Saldo'].map((h, i) => (
                                <th
                                    key={h}
                                    className={
                                        'bg-muted/40 px-1.5 py-1 text-[9px] font-bold uppercase tracking-wide text-muted-foreground ' +
                                        (i === 0 ? 'text-left' : 'text-right')
                                    }
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!result.valid ? (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-3 py-3 text-center text-muted-foreground"
                                >
                                    Ajusta precio, enganche y plazo para simular
                                </td>
                            </tr>
                        ) : (
                            result.weeks.map((row) => (
                                <tr key={row.week}>
                                    <td className="border-b border-border/40 px-1.5 py-1 text-left tabular-nums">
                                        {row.week}
                                    </td>
                                    <td className="border-b border-border/40 px-1.5 py-1 text-right tabular-nums">
                                        {formatMXN(row.payment)}
                                    </td>
                                    <td className="border-b border-border/40 px-1.5 py-1 text-right tabular-nums">
                                        {formatMXN(row.principal)}
                                    </td>
                                    <td className="border-b border-border/40 px-1.5 py-1 text-right tabular-nums">
                                        {formatMXN(row.interest)}
                                    </td>
                                    <td className="border-b border-border/40 px-1.5 py-1 text-right tabular-nums">
                                        {formatMXN(row.vat)}
                                    </td>
                                    <td className="border-b border-border/40 px-1.5 py-1 text-right tabular-nums">
                                        {formatMXN(row.balance)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    {result.valid ? (
                        <tfoot>
                            <tr className="border-t-2 border-border bg-muted/30 font-bold">
                                <td className="px-1.5 py-1 text-left">Total</td>
                                <td className="px-1.5 py-1 text-right tabular-nums">
                                    {formatMXN(result.totals.payment)}
                                </td>
                                <td className="px-1.5 py-1 text-right tabular-nums">
                                    {formatMXN(result.totals.principal)}
                                </td>
                                <td className="px-1.5 py-1 text-right tabular-nums">
                                    {formatMXN(result.totals.interest)}
                                </td>
                                <td className="px-1.5 py-1 text-right tabular-nums">
                                    {formatMXN(result.totals.vat)}
                                </td>
                                <td className="px-1.5 py-1 text-right tabular-nums">
                                    {formatMXN(0)}
                                </td>
                            </tr>
                        </tfoot>
                    ) : null}
                </table>
            </div>
        </div>
    );
}
